import { NgbDateStruct } from '@cmps/ng-bootstrap';

import { ModelSetTransformer, ModelTransformer } from '../common/common.model';
import { ExtText } from '../common/common.service';
import { Util } from '../common/common.util';

export const USER_LIST_MAX_COUNT = 50;

export interface ExtUserSecurityInfo {
    userName?: string;
    lockedOut: boolean;
    newPassword?: string;
    passwordMustChange: boolean;
    userId: number;
}

export interface ExtUserSuperGroup {
    usersSuperGroupId: number;
    superGroupId: number;
    startDate: string | Date;
    endDate: string | Date;
    text?: ExtText;
    tenantId?: number;
    userId: number;
    loggedInUserId?: number;
}

export interface ExtUserHierarchy {
    usersManagerId: number;
    managerId: number;
    hierarchyTypeId: number;
    subOrdinateName?: string;
    managerName?: string;
    startDate: string | Date;
    endDate: string | Date;
    tenantId?: number;
    userId: number;
    loggedInUserId?: number;
}

export interface ExtUserSystemId {
    userSystemId: number;
    systemIdType: number;
    systemUserId: string;
    systemParameter: string;
    startDate: string | Date;
    endDate: string | Date;
    tenantId?: number;
    userId: number;
    loggedInUserId?: number;
}

export interface ExtUserName {
    newUserName: string;
    userId: number;
}

export interface ExtUserRole {
    usersRoleId: number;
    roleId: number;
    startDate: string | Date;
    endDate: string | Date;
    tenantId?: number;
    userId: number;
    loggedInUserId?: number;
}

export interface ExtUserEmployeeType {
    usersEmployeeTypeID: number;
    employeeTypeId: number;
    startDate: string | Date;
    endDate: string | Date;
    tenantId?: number;
    userId: number;
    loggedInUserId?: number;
}

export interface ExtUserSite {
    usersSiteId: number;
    siteId: number;
    startDate: string | Date;
    endDate: string | Date;
    tenantId?: number;
    userId: number;
    loggedInUserId?: number;
}

// TODO refactor
interface ExtUserBase {
    userId?: number;
    firstName: string;
    middleName: string;
    lastName: string;
    email: string;
    phone: string;
    languageId: number;
    tenureDate: string | Date;
    startDate: string | Date;
    endDate: string | Date;
}

export interface ExtUserCreate extends ExtUserBase {
    role: ExtUserRole;
    superGroup?: ExtUserSuperGroup;
    manager?: ExtUserHierarchy;
    employeeType?: ExtUserEmployeeType;
    site: ExtUserSite;
    systemId?: ExtUserSystemId;
}

export interface ExtUser extends ExtUserBase {
    systemUserID?: string;
    role?: string;
    roleId?: number;
    managerName?: string;
    superGroup?: string;
    employeeType?: string;
}

export interface ExtSecurityQuestion {
    securityQuestionId: number;
    question: string;
    answer?: string;
    enabled: boolean;
    tenantId: number;
    userId: number;
}

export enum AccountStatus {
    LOCKED,
    UNLOCKED,
}

export class UserSecurityInfo {
    username: string;
    accountStatus: AccountStatus;
    password?: string;
    passwordConfirm?: string;
    forcePasswordReset: boolean;
    userId?: number;

    constructor() {
        this.accountStatus = AccountStatus.UNLOCKED;
        this.forcePasswordReset = true;
    }

    copy(): UserSecurityInfo {
        const si = new UserSecurityInfo();
        si.username = this.username;
        si.accountStatus = this.accountStatus;
        si.forcePasswordReset = this.forcePasswordReset;
        si.userId = this.userId;
        return si;
    }

    merge(that: UserSecurityInfo): void {
        this.username = that.username != null ? that.username : this.username;
        this.accountStatus =
            that.accountStatus != null
                ? that.accountStatus
                : this.accountStatus;
        this.forcePasswordReset =
            that.forcePasswordReset != null
                ? that.forcePasswordReset
                : this.forcePasswordReset;
        this.userId = that.userId != null ? that.userId : this.userId;
    }
}

export class UserSuperGroup {
    id: number;
    superGroupId: number;
    startDate: Date | NgbDateStruct;
    endDate: Date | NgbDateStruct;
    userSuperGroupName?: string;
    userId: number;

    merge(that: UserSuperGroup, ngbDate: boolean = false): void {
        this.id = that.id != null ? that.id : this.id;
        this.superGroupId =
            that.superGroupId != null ? that.superGroupId : this.superGroupId;
        this.startDate =
            that.startDate != null ? that.startDate : this.startDate;
        this.endDate = that.endDate != null ? that.endDate : this.endDate;
        this.userSuperGroupName =
            that.userSuperGroupName != null
                ? that.userSuperGroupName
                : this.userSuperGroupName;
        this.userId = that.userId != null ? that.userId : this.userId;

        this.startDate = ngbDate
            ? Util.dateToNgbDate(that.startDate)
            : Util.dateToDate(that.startDate);
        this.endDate = ngbDate
            ? Util.dateToNgbDate(that.endDate)
            : Util.dateToDate(that.endDate);
    }
}

export class UserHierarchy {
    id: number;
    managerId: number;
    managerName: string;
    hierarchyTypeId: number;
    startDate: Date | NgbDateStruct;
    endDate: Date | NgbDateStruct;
    userId: number;

    constructor() {
        this.managerName = null;
    }

    merge(that: UserHierarchy, ngbDate: boolean = false): void {
        this.id = that.id != null ? that.id : this.id;
        this.managerId =
            that.managerId != null ? that.managerId : this.managerId;
        this.managerName =
            that.managerName != null ? that.managerName : this.managerName;
        this.hierarchyTypeId =
            that.hierarchyTypeId != null
                ? that.hierarchyTypeId
                : this.hierarchyTypeId;
        this.startDate =
            that.startDate != null ? that.startDate : this.startDate;
        this.endDate = that.endDate != null ? that.endDate : this.endDate;
        this.userId = that.userId != null ? that.userId : this.userId;

        this.startDate = ngbDate
            ? Util.dateToNgbDate(that.startDate)
            : Util.dateToDate(that.startDate);
        this.endDate = ngbDate
            ? Util.dateToNgbDate(that.endDate)
            : Util.dateToDate(that.endDate);
    }
}

export class UserSystemId {
    id: number;
    systemIdTypeId: number;
    systemId: string;
    systemParameter: string;
    startDate: Date | NgbDateStruct;
    endDate: Date | NgbDateStruct;
    userId: number;

    merge(that: UserSystemId, ngbDate: boolean = false): void {
        this.id = that.id != null ? that.id : this.id;
        this.systemIdTypeId =
            that.systemIdTypeId != null
                ? that.systemIdTypeId
                : this.systemIdTypeId;
        this.systemId = that.systemId != null ? that.systemId : this.systemId;
        this.systemParameter =
            that.systemParameter != null
                ? that.systemParameter
                : this.systemParameter;
        this.startDate =
            that.startDate != null ? that.startDate : this.startDate;
        this.endDate = that.endDate != null ? that.endDate : this.endDate;
        this.userId = that.userId != null ? that.userId : this.userId;

        this.startDate = ngbDate
            ? Util.dateToNgbDate(that.startDate)
            : Util.dateToDate(that.startDate);
        this.endDate = ngbDate
            ? Util.dateToNgbDate(that.endDate)
            : Util.dateToDate(that.endDate);
    }
}

export class UserRole {
    id: number;
    roleId: number;
    startDate: Date | NgbDateStruct;
    endDate: Date | NgbDateStruct;
    userId: number;

    merge(that: UserRole, ngbDate: boolean = false): void {
        this.id = that.id != null ? that.id : this.id;
        this.roleId = that.roleId != null ? that.roleId : this.roleId;
        this.startDate =
            that.startDate != null ? that.startDate : this.startDate;
        this.endDate = that.endDate != null ? that.endDate : this.endDate;
        this.userId = that.userId != null ? that.userId : this.userId;

        this.startDate = ngbDate
            ? Util.dateToNgbDate(that.startDate)
            : Util.dateToDate(that.startDate);
        this.endDate = ngbDate
            ? Util.dateToNgbDate(that.endDate)
            : Util.dateToDate(that.endDate);
    }
}

export class UserEmployeeType {
    id: number;
    employeeTypeId: number;
    startDate: Date | NgbDateStruct;
    endDate: Date | NgbDateStruct;
    userId: number;

    merge(that: UserEmployeeType, ngbDate: boolean = false): void {
        this.id = that.id != null ? that.id : this.id;
        this.employeeTypeId =
            that.employeeTypeId != null
                ? that.employeeTypeId
                : this.employeeTypeId;
        this.startDate =
            that.startDate != null ? that.startDate : this.startDate;
        this.endDate = that.endDate != null ? that.endDate : this.endDate;
        this.userId = that.userId != null ? that.userId : this.userId;

        this.startDate = ngbDate
            ? Util.dateToNgbDate(that.startDate)
            : Util.dateToDate(that.startDate);
        this.endDate = ngbDate
            ? Util.dateToNgbDate(that.endDate)
            : Util.dateToDate(that.endDate);
    }
}

export class UserSite {
    id: number;
    siteId: number;
    startDate: Date | NgbDateStruct;
    endDate: Date | NgbDateStruct;
    userId: number;

    merge(that: UserSite, ngbDate: boolean = false): void {
        this.id = that.id != null ? that.id : this.id;
        this.siteId = that.siteId != null ? that.siteId : this.siteId;
        this.startDate =
            that.startDate != null ? that.startDate : this.startDate;
        this.endDate = that.endDate != null ? that.endDate : this.endDate;
        this.userId = that.userId != null ? that.userId : this.userId;

        this.startDate = ngbDate
            ? Util.dateToNgbDate(that.startDate)
            : Util.dateToDate(that.startDate);
        this.endDate = ngbDate
            ? Util.dateToNgbDate(that.endDate)
            : Util.dateToDate(that.endDate);
    }
}

export class User {
    systemUserId: string;
    role: string;
    roleId: number;
    managerName: string;
    superGroup: string;
    employeeType: string;
    id: number;
    firstName: string;
    middleName: string;
    lastName: string;
    email: string;
    phone: string;
    languageId: number;
    tenureDate: Date | NgbDateStruct;
    startDate: Date | NgbDateStruct;
    endDate: Date | NgbDateStruct;

    securityInfo: UserSecurityInfo;
    superGroups: UserSuperGroup[];
    managers: UserHierarchy[];
    subordinates: UserHierarchy[];
    systemIds: UserSystemId[];
    roles: UserRole[];
    employeeTypes: UserEmployeeType[];
    sites: UserSite[];

    constructor() {
        this.id = -1;

        this.superGroups = [];
        this.managers = [];
        this.subordinates = [];
        this.systemIds = [];
        this.roles = [];
        this.employeeTypes = [];
        this.sites = [];
    }

    copy(ngbDate: boolean = false): User {
        const u = new User();
        u.systemUserId = this.systemUserId;
        u.role = this.role;
        u.roleId = this.roleId;
        u.managerName = this.managerName;
        u.superGroup = this.superGroup;
        u.employeeType = this.employeeType;
        u.id = this.id;
        u.firstName = this.firstName;
        u.middleName = this.middleName;
        u.lastName = this.lastName;
        u.email = this.email;
        u.phone = this.phone;
        u.languageId = this.languageId;
        u.tenureDate = ngbDate
            ? Util.dateToNgbDate(this.tenureDate)
            : Util.dateToDate(this.tenureDate);
        u.startDate = ngbDate
            ? Util.dateToNgbDate(this.startDate)
            : Util.dateToDate(this.startDate);
        u.endDate = ngbDate
            ? Util.dateToNgbDate(this.endDate)
            : Util.dateToDate(this.endDate);
        return u;
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    merge(that: User, ngbDate: boolean = false) {
        this.systemUserId =
            that.systemUserId != null ? that.systemUserId : this.systemUserId;
        this.role = that.role != null ? that.role : this.role;
        this.roleId = that.roleId != null ? that.roleId : this.roleId;
        this.managerName =
            that.managerName != null ? that.managerName : this.managerName;
        this.superGroup =
            that.superGroup != null ? that.superGroup : this.superGroup;
        this.employeeType =
            that.employeeType != null ? that.employeeType : this.employeeType;
        this.id = that.id != null ? that.id : this.id;
        this.firstName =
            that.firstName != null ? that.firstName : this.firstName;
        this.middleName =
            that.middleName != null ? that.middleName : this.middleName;
        this.lastName = that.lastName != null ? that.lastName : this.lastName;
        this.email = that.email != null ? that.email : this.email;
        this.phone = that.phone != null ? that.phone : this.phone;
        this.languageId =
            that.languageId != null ? that.languageId : this.languageId;

        this.tenureDate = ngbDate
            ? Util.dateToNgbDate(that.tenureDate)
            : Util.dateToDate(that.tenureDate);
        this.startDate = ngbDate
            ? Util.dateToNgbDate(that.startDate)
            : Util.dateToDate(that.startDate);
        this.endDate = ngbDate
            ? Util.dateToNgbDate(that.endDate)
            : Util.dateToDate(that.endDate);
    }
}

export interface SecurityQuestion {
    id: number;
    question: string;
    answer?: string;
    enabled: boolean;
    tenantId: number;
    userId: number;
}

export class UserSecurityInfoModelTransformer
    implements ModelTransformer<UserSecurityInfo, ExtUserSecurityInfo>
{
    constructor(private readonly _createMode: boolean = false) {}

    transform(model: ExtUserSecurityInfo): UserSecurityInfo {
        const transformed = new UserSecurityInfo();
        transformed.username = model.userName;
        transformed.accountStatus =
            model.lockedOut === true
                ? AccountStatus.LOCKED
                : AccountStatus.UNLOCKED;
        transformed.forcePasswordReset = model.passwordMustChange;
        return transformed;
    }

    reverseTransform(model: UserSecurityInfo): ExtUserSecurityInfo {
        const transformed: ExtUserSecurityInfo = {
            lockedOut: model.accountStatus === AccountStatus.LOCKED,
            newPassword: model.password,
            passwordMustChange: model.forcePasswordReset,
            userId: model.userId,
        };
        if (this._createMode) {
            transformed.userName = !model.username
                ? model.username
                : model.username.trim();
        }
        return transformed;
    }
}

export class UserSuperGroupModelTransformer
    implements ModelTransformer<UserSuperGroup, ExtUserSuperGroup>
{
    transform(model: ExtUserSuperGroup): UserSuperGroup {
        const transformed = new UserSuperGroup();
        transformed.id = model.usersSuperGroupId;
        transformed.superGroupId = model.superGroupId;
        transformed.startDate = Util.dateToDate(model.startDate);
        transformed.endDate = Util.dateToDate(model.endDate);
        transformed.userSuperGroupName = !model.text
            ? null
            : model.text.shortName; // TODO text is null during creation
        transformed.userId = model.userId;
        return transformed;
    }

    reverseTransform(model: UserSuperGroup): ExtUserSuperGroup {
        return {
            usersSuperGroupId: model.id,
            superGroupId: model.superGroupId,
            startDate: Util.dateToDate(model.startDate),
            endDate: Util.dateToDate(model.endDate),
            userId: model.userId,
        };
    }
}

export class UserSuperGroupModelSetTransformer extends ModelSetTransformer<
    UserSuperGroup,
    ExtUserSuperGroup
> {
    protected newTransformer(): ModelTransformer<
        UserSuperGroup,
        ExtUserSuperGroup
    > {
        return new UserSuperGroupModelTransformer();
    }
}

export class UserHierarchyModelTransformer
    implements ModelTransformer<UserHierarchy, ExtUserHierarchy>
{
    transform(model: ExtUserHierarchy): UserHierarchy {
        const transformed = new UserHierarchy();
        transformed.id = model.usersManagerId;
        transformed.managerId = model.managerId;
        transformed.managerName = model.managerName || model.subOrdinateName;
        transformed.hierarchyTypeId = model.hierarchyTypeId;
        transformed.startDate = Util.dateToDate(model.startDate);
        transformed.endDate = Util.dateToDate(model.endDate);
        transformed.userId = model.userId;
        return transformed;
    }

    reverseTransform(model: UserHierarchy): ExtUserHierarchy {
        return {
            usersManagerId: model.id,
            managerId: model.managerId,
            hierarchyTypeId: model.hierarchyTypeId,
            startDate: Util.dateToDate(model.startDate),
            endDate: Util.dateToDate(model.endDate),
            userId: model.userId,
        };
    }
}

export class UserHierarchyModelSetTransformer extends ModelSetTransformer<
    UserHierarchy,
    ExtUserHierarchy
> {
    protected newTransformer(): ModelTransformer<
        UserHierarchy,
        ExtUserHierarchy
    > {
        return new UserHierarchyModelTransformer();
    }
}

export class UserSystemIdModelTransformer
    implements ModelTransformer<UserSystemId, ExtUserSystemId>
{
    transform(model: ExtUserSystemId): UserSystemId {
        const transformed = new UserSystemId();
        transformed.id = model.userSystemId;
        transformed.systemIdTypeId = model.systemIdType;
        transformed.systemId = model.systemUserId;
        transformed.systemParameter = model.systemParameter;
        transformed.startDate = Util.dateToDate(model.startDate);
        transformed.endDate = Util.dateToDate(model.endDate);
        transformed.userId = model.userId;
        return transformed;
    }

    reverseTransform(model: UserSystemId): ExtUserSystemId {
        return {
            userSystemId: model.id,
            systemIdType: model.systemIdTypeId,
            systemUserId: model.systemId,
            systemParameter: model.systemParameter,
            startDate: Util.dateToDate(model.startDate),
            endDate: Util.dateToDate(model.endDate),
            userId: model.userId,
        };
    }
}

export class UserSystemIdModelSetTransformer extends ModelSetTransformer<
    UserSystemId,
    ExtUserSystemId
> {
    protected newTransformer(): ModelTransformer<
        UserSystemId,
        ExtUserSystemId
    > {
        return new UserSystemIdModelTransformer();
    }
}

export class UserRoleModelTransformer
    implements ModelTransformer<UserRole, ExtUserRole>
{
    transform(model: ExtUserRole): UserRole {
        const transformed = new UserRole();
        transformed.id = model.usersRoleId;
        transformed.roleId = model.roleId;
        transformed.startDate = Util.dateToDate(model.startDate);
        transformed.endDate = Util.dateToDate(model.endDate);
        transformed.userId = model.userId;
        return transformed;
    }

    reverseTransform(model: UserRole): ExtUserRole {
        return {
            usersRoleId: model.id,
            roleId: model.roleId,
            startDate: Util.dateToDate(model.startDate),
            endDate: Util.dateToDate(model.endDate),
            userId: model.userId,
        };
    }
}

export class UserRoleModelSetTransformer extends ModelSetTransformer<
    UserRole,
    ExtUserRole
> {
    protected newTransformer(): ModelTransformer<UserRole, ExtUserRole> {
        return new UserRoleModelTransformer();
    }
}

export class UserEmployeeTypeModelTransformer
    implements ModelTransformer<UserEmployeeType, ExtUserEmployeeType>
{
    transform(model: ExtUserEmployeeType): UserEmployeeType {
        const transformed = new UserEmployeeType();
        transformed.id = model.usersEmployeeTypeID;
        transformed.employeeTypeId = model.employeeTypeId;
        transformed.startDate = Util.dateToDate(model.startDate);
        transformed.endDate = Util.dateToDate(model.endDate);
        transformed.userId = model.userId;
        return transformed;
    }

    reverseTransform(model: UserEmployeeType): ExtUserEmployeeType {
        return {
            usersEmployeeTypeID: model.id,
            employeeTypeId: model.employeeTypeId,
            startDate: Util.dateToDate(model.startDate),
            endDate: Util.dateToDate(model.endDate),
            userId: model.userId,
        };
    }
}

export class UserEmployeeTypeModelSetTransformer extends ModelSetTransformer<
    UserEmployeeType,
    ExtUserEmployeeType
> {
    protected newTransformer(): ModelTransformer<
        UserEmployeeType,
        ExtUserEmployeeType
    > {
        return new UserEmployeeTypeModelTransformer();
    }
}

export class UserSiteModelTransformer
    implements ModelTransformer<UserSite, ExtUserSite>
{
    transform(model: ExtUserSite): UserSite {
        const transformed = new UserSite();
        transformed.id = model.usersSiteId;
        transformed.siteId = model.siteId;
        transformed.startDate = Util.dateToDate(model.startDate);
        transformed.endDate = Util.dateToDate(model.endDate);
        transformed.userId = model.userId;
        return transformed;
    }

    reverseTransform(model: UserSite): ExtUserSite {
        return {
            usersSiteId: model.id,
            siteId: model.siteId,
            startDate: Util.dateToDate(model.startDate),
            endDate: Util.dateToDate(model.endDate),
            userId: model.userId,
        };
    }
}

export class UserSiteModelSetTransformer extends ModelSetTransformer<
    UserSite,
    ExtUserSite
> {
    protected newTransformer(): ModelTransformer<UserSite, ExtUserSite> {
        return new UserSiteModelTransformer();
    }
}

export class UserCreateModelTransformer
    implements ModelTransformer<User, ExtUserCreate>
{
    reverseTransform(model: User): ExtUserCreate {
        const user: ExtUserCreate = {
            firstName: model.firstName,
            middleName: model.middleName,
            lastName: model.lastName,
            email: model.email,
            phone: model.phone,
            languageId: model.languageId,
            tenureDate: Util.dateToDate(model.tenureDate),
            startDate: Util.dateToDate(model.startDate),
            endDate: Util.dateToDate(model.endDate),
            role: new UserRoleModelTransformer().reverseTransform(
                model.roles[0],
            ),
            site: new UserSiteModelTransformer().reverseTransform(
                model.sites[0],
            ),
        };
        // check one required field
        if (
            model.superGroups &&
            model.superGroups.length !== 0 &&
            model.superGroups[0].superGroupId != null
        ) {
            user.superGroup =
                new UserSuperGroupModelTransformer().reverseTransform(
                    model.superGroups[0],
                );
        }
        if (
            model.managers &&
            model.managers.length !== 0 &&
            model.managers[0].managerId != null
        ) {
            user.manager = new UserHierarchyModelTransformer().reverseTransform(
                model.managers[0],
            );
        }
        if (
            model.employeeTypes &&
            model.employeeTypes.length !== 0 &&
            model.employeeTypes[0].employeeTypeId != null
        ) {
            user.employeeType =
                new UserEmployeeTypeModelTransformer().reverseTransform(
                    model.employeeTypes[0],
                );
        }
        if (
            model.systemIds &&
            model.systemIds.length !== 0 &&
            model.systemIds[0].systemId != null
        ) {
            user.systemId = new UserSystemIdModelTransformer().reverseTransform(
                model.systemIds[0],
            );
        }
        return user;
    }
}

export class UserModelTransformer implements ModelTransformer<User, ExtUser> {
    transform(model: ExtUser): User {
        const transformed = new User();
        transformed.systemUserId = model.systemUserID;
        transformed.role = model.role;
        transformed.roleId = model.roleId;
        transformed.managerName = model.managerName;
        transformed.superGroup = model.superGroup;
        transformed.employeeType = model.employeeType;
        transformed.id = model.userId;
        transformed.firstName = model.firstName;
        transformed.middleName = model.middleName;
        transformed.lastName = model.lastName;
        transformed.email = model.email;
        transformed.phone = model.phone;
        transformed.languageId = model.languageId;
        transformed.tenureDate = Util.dateToDate(model.tenureDate);
        transformed.startDate = Util.dateToDate(model.startDate);
        transformed.endDate = Util.dateToDate(model.endDate);
        return transformed;
    }

    reverseTransform(model: User): ExtUser {
        return {
            userId: model.id,
            firstName: model.firstName,
            middleName: model.middleName,
            lastName: model.lastName,
            email: model.email,
            phone: model.phone,
            languageId: model.languageId,
            tenureDate: Util.dateToDate(model.tenureDate),
            startDate: Util.dateToDate(model.startDate),
            endDate: Util.dateToDate(model.endDate),
        };
    }
}

export class UserModelSetTransformer
    implements ModelTransformer<User[], ExtUser[]>
{
    transform(model: ExtUser[]): User[] {
        const transformer = new UserModelTransformer();
        const transformed: User[] = [];
        model.forEach((item) => transformed.push(transformer.transform(item)));
        return transformed;
    }

    reverseTransform(model: User[]): ExtUser[] {
        return null;
    }
}

export class SecurityQuestionModelTransformer
    implements ModelTransformer<SecurityQuestion, ExtSecurityQuestion>
{
    transform(model: ExtSecurityQuestion): SecurityQuestion {
        return {
            id: model.securityQuestionId,
            question: model.question,
            enabled: model.enabled,
            tenantId: model.tenantId,
            userId: model.userId,
        };
    }

    reverseTransform(model: SecurityQuestion): ExtSecurityQuestion {
        return {
            securityQuestionId: model.id,
            question: model.question,
            answer: model.answer,
            enabled: model.enabled,
            tenantId: model.tenantId,
            userId: model.userId,
        };
    }
}
